import type { NavigateToOptions } from 'nuxt/dist/app/composables/router'
import { isString } from '@develop/ia-utils'
import type { RouteLocation } from '#vue-router'

type NamedRoute = RouteLocation['name']

type CustomNavigateToOptions = Exclude<NavigateToOptions, 'external'>

export const routesExcludedFromExternal: ([NamedRoute] | [NamedRoute, NamedRoute])[] = []

export const shouldNavigateExternally = (to: RouteLocationRaw | undefined | null) => {
  if (!to)
    return false

  const { forceExternalLinks } = useEnvs()

  const targetRouteName = routeResolver(to)

  return forceExternalLinks.value && !routesExcludedFromExternal.flat().includes(targetRouteName as NamedRoute)
}

/**
 * Cases handled:
 *    customNavigateTo('https://www.google.com')
 *    customNavigateTo({ path: 'https://www.google.com' })
 *    customNavigateTo('/product/123')
 *    customNavigateTo({ name: 'product', params: { id: 123 } })
 *    customNavigateTo({ path: '/product/123', {} })
 *
 * Cases not handled:
 *    customNavigateTo('https://www.google.com', { external: true })
 */
export const customNavigateTo = (to: RouteLocationRaw | undefined | null, options: CustomNavigateToOptions = {}) => {
  let route = to

  if (!route)
    return

  let isExternal = isString(to) ? isAbsoluteUrl(to) : isExternalPath(to as RouteLocation)

  if (!isExternal) {
    isExternal = shouldNavigateExternally(to)

    if (isExternal) {
      const url = useRequestURL()
      const targetRoute = useRouter().resolve(route)
      route = `${url.origin}${targetRoute.href}`
    }
  }

  return navigateTo(route, { ...options, external: isExternal })
}
